const LAST_LOGIN_METHOD_KEY = 'lastLoginMethod';

export type LoginMethod = 'email' | 'Google' | 'Apple' | 'Facebook';

/**
 * Stores the last login method used by the user in localStorage
 */
export const saveLastLoginMethod = (method: LoginMethod): void => {
  try {
    localStorage.setItem(
      LAST_LOGIN_METHOD_KEY,
      JSON.stringify({
        method,
        timestamp: new Date().toISOString(),
      }),
    );
  } catch (error) {
    // Silently fail if localStorage is not available
    // biome-ignore lint/suspicious/noConsole: <explanation>
    console.warn('Failed to save last login method:', error);
  }
};

/**
 * Retrieves the last login method used by the user from localStorage
 */
export const getLastLoginMethod = (): LoginMethod | null => {
  try {
    const stored = localStorage.getItem(LAST_LOGIN_METHOD_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    return parsed.method || null;
  } catch (error) {
    // Silently fail if localStorage is not available or data is corrupted
    // biome-ignore lint/suspicious/noConsole: <explanation>
    console.warn('Failed to get last login method:', error);
    return null;
  }
};

/**
 * Builds the i18n-aware hint message for the last login method.
 * Returns null when no method is stored.
 */
export const getLastLoginMethodHint = (
  method: LoginMethod | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null => {
  if (!method) return null;
  return t('lastLoginMethod.hint', { method: t(`lastLoginMethod.methods.${method}`) });
};
